'use client';

import React, { useEffect, useRef, useState } from 'react';
import { parseMarkdownBlocks } from './message';

const MESSAGE_SPEED_MS = 8;
const PROMPT_SPEED_MS = 12;

interface DiscussionTurnTextProps {
  message: string | null | undefined;
  prompt: string | null | undefined;
  turnId: string;
  /** Only the active (latest) turn animates; history renders instantly. */
  isLatest: boolean;
  /**
   * Restored/resumed turn: render fully-formed with no typewriter replay, but still
   * report completion (unlike `!isLatest` history, which never completes) so the
   * thread's answer chips appear right away.
   */
  instant?: boolean;
  isLoading: boolean;
  onStreamComplete?: () => void;
  /** Fired as the typewriter advances so the thread can pin scroll. */
  onProgress?: () => void;
  /**
   * Rich content between the message and the prompt (the comparison card). It
   * appears once the message has finished typing, so it reads as the answer to
   * the headline above it, and the prompt then types below it.
   */
  between?: React.ReactNode;
}

/**
 * Renders a discussion assistant turn. Historical turns show full text immediately.
 * The latest turn typewrites message then prompt (same pacing as PromptBanner) so
 * content is revealed incrementally even when the model delivers JSON in large chunks.
 */
export function DiscussionTurnText({
  message,
  prompt,
  turnId,
  isLatest,
  instant = false,
  isLoading,
  onStreamComplete,
  onProgress,
  between,
}: DiscussionTurnTextProps) {
  const fullMessage = message ?? '';
  const fullPrompt = prompt ?? '';
  const turnText = [fullMessage, fullPrompt].filter(Boolean).join('\n\n');

  const [phase, setPhase] = useState<'message' | 'prompt'>('message');
  const [visibleChars, setVisibleChars] = useState(0);
  const previousTurnIdRef = useRef(turnId);
  const completedRef = useRef(false);

  useEffect(() => {
    if (turnId !== previousTurnIdRef.current) {
      previousTurnIdRef.current = turnId;
      setPhase('message');
      setVisibleChars(0);
      completedRef.current = false;
    }
  }, [turnId]);

  useEffect(() => {
    if (!isLatest) return;
    if (phase === 'message' && !fullMessage && fullPrompt) {
      setPhase('prompt');
      setVisibleChars(0);
    }
  }, [isLatest, phase, fullMessage, fullPrompt]);

  const currentText = phase === 'message' ? fullMessage : fullPrompt;
  const currentSpeed = phase === 'message' ? MESSAGE_SPEED_MS : PROMPT_SPEED_MS;

  useEffect(() => {
    if (!isLatest) return;
    if (!currentText) return;
    if (visibleChars >= currentText.length) return;
    const t = setTimeout(() => setVisibleChars((v) => v + 1), currentSpeed);
    return () => clearTimeout(t);
  }, [isLatest, currentText, visibleChars, currentSpeed]);

  useEffect(() => {
    if (!isLatest) return;
    if (phase !== 'message') return;
    if (!fullMessage) return;
    if (visibleChars < fullMessage.length) return;
    if (!fullPrompt) return;
    setPhase('prompt');
    setVisibleChars(0);
  }, [isLatest, phase, fullMessage, fullPrompt, visibleChars]);

  const typingCaughtUp =
    phase === 'message'
      ? visibleChars >= fullMessage.length
      : visibleChars >= fullPrompt.length;

  useEffect(() => {
    if (!isLatest) return;
    if (completedRef.current) return;
    // Restored turn: complete immediately, without waiting on a typewriter that
    // never runs (the text below renders in full).
    if (!instant) {
      if (isLoading) return;
      if (!typingCaughtUp) return;
    }
    completedRef.current = true;
    onStreamComplete?.();
  }, [isLatest, instant, isLoading, typingCaughtUp, onStreamComplete]);

  useEffect(() => {
    if (!isLatest) return;
    onProgress?.();
  }, [isLatest, visibleChars, phase, onProgress, turnId, currentText.length, fullMessage.length, fullPrompt.length, isLoading]);

  if (between) {
    // Split layout: message, the rich block, then the prompt. Same spacing as
    // the paragraph blocks parseMarkdownBlocks renders on its own.
    const settled = !isLatest || instant;
    const shownMessage = settled || phase === 'prompt' ? fullMessage : fullMessage.substring(0, visibleChars);
    const messageTyped = settled || phase === 'prompt' || visibleChars >= fullMessage.length;
    const shownPrompt = settled ? fullPrompt : phase === 'prompt' ? fullPrompt.substring(0, visibleChars) : '';
    return (
      <div className="space-y-4">
        {shownMessage ? parseMarkdownBlocks(shownMessage) : null}
        {messageTyped ? between : null}
        {shownPrompt ? parseMarkdownBlocks(shownPrompt) : null}
      </div>
    );
  }

  if (!isLatest || instant) {
    return <>{parseMarkdownBlocks(turnText)}</>;
  }

  let displayText = '';
  if (phase === 'message') {
    displayText = fullMessage.substring(0, visibleChars);
  } else {
    const parts: string[] = [];
    if (fullMessage) parts.push(fullMessage);
    if (fullPrompt) parts.push(fullPrompt.substring(0, visibleChars));
    displayText = parts.join('\n\n');
  }

  return <>{parseMarkdownBlocks(displayText || null)}</>;
}
